import { lazy, Suspense, useState } from "react";
import { Button, Form, Modal, Space, Spin, Tag } from "antd";
import { ImportOutlined, PlusOutlined } from "@ant-design/icons";
import { ListPage } from "../../components/ListPage";
import { SiteLabel } from "../../components/SiteLabel";
import { SiteSelect } from "../../components/SiteSelect";
import { StaffFormFields } from "./StaffFormFields";
import { staffFormToRequest } from "./staffForm";
import { useApi } from "../../helpers/useApi";
import { useSitesQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import type { Staff } from "../../api-models";

const StaffImportDialog = lazy(() => import("./StaffImportDialog"));

// Where a person is posted today: the server sends only the assignment(s) effective now.
function currentSite(r: Staff) {
  const a = r.assignments?.[0];
  return a ? <SiteLabel site={a.site} project={a.project} /> : "Unassigned";
}

// Staff is not User (spec §28.2) — site labourers have no login identity. This is HR's
// master list; effective site assignments are managed on StaffDetailPage.
export default function StaffListPage() {
  const { user } = useUser();
  const { data: sites = [] } = useSitesQuery();
  const canWrite = hasPermission(user, "Staff.Write");
  const { request } = useApi();
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);

  async function handleSubmit() {
    const values = await form.validateFields();
    await request({ method: "POST", url: "/staff", data: staffFormToRequest(values) });
    setOpen(false);
    setReloadKey((k) => k + 1);
  }

  return (
    <>
      <ListPage<Staff>
        key={reloadKey}
        title="Staff"
        endpoint="/staff"
        searchPlaceholder="Search staff..."
        linkTo={(r) => `/staff/${r.id}`}
        actions={
          canWrite && (
            <Space>
              <Button icon={<ImportOutlined />} onClick={() => setImporting(true)} data-tour="staff-import">
                Import
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  form.resetFields();
                  setOpen(true);
                }}
              >
                New staff
              </Button>
            </Space>
          )
        }
        filters={(controls) => (
          // A blank site is a real answer here (every site's staff, unassigned included),
          // so the picker never fills itself in.
          <SiteSelect
            allowClear
            showSearch
            optionFilterProp="label"
            autoSelectSingle={false}
            placeholder="Current site"
            style={{ width: 240 }}
            data-tour="staff-site-filter"
            options={sites.map((s) => ({ value: s.id, label: s.name }))}
            value={controls.num("siteId")}
            onChange={(v) => controls.set("siteId", v)}
          />
        )}
        columns={[
          { title: "Staff Code", dataIndex: "staffCode", sorter: true },
          { title: "Name", dataIndex: "name", sorter: true },
          { title: "Designation", dataIndex: "designation", sorter: true },
          { title: "Current site", key: "currentSite", render: (_, r) => currentSite(r) },
          { title: "Nationality", dataIndex: "nationality", sorter: true },
          {
            title: "Status",
            dataIndex: "employmentStatus",
            sorter: true,
            render: (v) => <Tag color={v === "ACTIVE" ? "green" : "default"}>{v}</Tag>,
          },
        ]}
        renderCard={(r) => (
          <div>
            <strong>{r.name}</strong>
            <div>
              {r.staffCode}
              {r.designation ? ` - ${r.designation}` : ""}
            </div>
            <div>{currentSite(r)}</div>
          </div>
        )}
      />
      <Modal title="New staff" open={open} onCancel={() => setOpen(false)} onOk={handleSubmit} destroyOnHidden>
        <Form form={form} layout="vertical">
          <StaffFormFields />
        </Form>
      </Modal>
      {importing && (
        <Suspense fallback={<Spin fullscreen />}>
          <StaffImportDialog
            onClose={() => setImporting(false)}
            onImported={() => {
              setImporting(false);
              setReloadKey((k) => k + 1);
            }}
          />
        </Suspense>
      )}
    </>
  );
}
